import { SUPPORTED_LOCALES, type Locale } from '@syncanix/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { localizedPath, stripLocalePrefix } from '@/i18n/locale-path';

import type { KeyboardEvent as ReactKeyboardEvent, ReactElement } from 'react';

/**
 * Marketing-site language picker. Lists every {@link SUPPORTED_LOCALES} entry by
 * its **native** name (shown in its own script, never translated) and calls
 * `setLocale`, which re-runs the `<html dir>` effect so an RTL choice flips the
 * whole page immediately. Closes on outside click, Escape, or selection.
 */
export function LanguageSwitcher(): ReactElement {
  const { t, locale, setLocale } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Roving-tabindex focus index within the open menu (APG menu keyboard model — I108).
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Every marketing route is published per-locale (GA-13 / ADR-0133) and the URL
  // is the source of truth for the language, so switching always navigates to the
  // sibling locale's URL — keeping the page crawlable and the canonical correct.
  // `setLocale` updates the catalog immediately (no flash); `LocaleSync` would
  // otherwise reconcile it from the new URL anyway.
  const onSelect = useCallback(
    (code: Locale) => {
      setLocale(code);
      setOpen(false);
      void navigate(localizedPath(stripLocalePrefix(pathname), code));
    },
    [navigate, pathname, setLocale],
  );

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // On open, move focus to the checked (or first) option — the APG menu pattern (I108).
  useEffect(() => {
    if (!open) return;
    const checked = SUPPORTED_LOCALES.findIndex((l) => l.code === locale);
    const idx = checked >= 0 ? checked : 0;
    setActiveIndex(idx);
    optionRefs.current[idx]?.focus();
  }, [open, locale]);

  // Arrow-key roving among the menu options (Down/Up wrap, Home/End jump).
  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLUListElement>): void => {
    const n = SUPPORTED_LOCALES.length;
    let next = activeIndex;
    if (e.key === 'ArrowDown') next = (activeIndex + 1) % n;
    else if (e.key === 'ArrowUp') next = (activeIndex - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else return;
    e.preventDefault();
    setActiveIndex(next);
    optionRefs.current[next]?.focus();
  };

  const active = SUPPORTED_LOCALES.find((l) => l.code === locale);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('lang.switcherLabel')}
        onClick={() => {
          setOpen((p) => !p);
        }}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-[color:var(--bd-body)] transition-colors hover:bg-[color:var(--bd-sunk)] hover:text-[color:var(--bd-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2"
      >
        {/* A stroke globe rather than the 🌐 emoji: the emoji is the one
            glyph on the site whose rendering the design does not control —
            it is a flat blue outline on Windows, a coloured sphere on Apple,
            and something else again on Android — and nothing else in the
            chrome uses emoji at all. */}
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="size-[15px] shrink-0">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3" />
          <path
            d="M2 8h12M8 2a9.5 9.5 0 0 1 0 12a9.5 9.5 0 0 1 0-12Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        {/* The label drops below `sm` for the same reason the wordmark does:
            at 390px the German row overflowed the bar. `aria-label` on the
            trigger already names the control, and the selected language is
            still marked in the open menu — so an icon-only trigger loses
            nothing but the width. */}
        <span lang={active?.code} className="hidden sm:inline">
          {active?.nativeName ?? 'Language'}
        </span>
      </button>
      {open ? (
        <ul
          role="menu"
          aria-label={t('lang.switcherLabel')}
          onKeyDown={onMenuKeyDown}
          className="absolute end-0 z-50 mt-2 w-48 rounded-[14px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] p-1.5 shadow-[0_28px_60px_-24px_rgba(12,13,16,.34),0_4px_14px_-8px_rgba(12,13,16,.16)]"
        >
          {SUPPORTED_LOCALES.map((meta, index) => {
            const isActive = meta.code === locale;
            return (
              <li key={meta.code} role="none">
                <button
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  // Roving tabindex: only the actively-focused option is in the Tab order.
                  tabIndex={index === activeIndex ? 0 : -1}
                  lang={meta.code}
                  onClick={() => {
                    onSelect(meta.code);
                  }}
                  className={`flex w-full items-center gap-2 rounded-[9px] px-2.5 py-2 text-sm transition-colors duration-150 hover:bg-[color:var(--bd-sunk)] focus-visible:bg-[color:var(--bd-sunk)] focus-visible:outline-none ${
                    isActive
                      ? 'bg-[color:var(--bd-tint)] font-medium text-[color:var(--bd-ink)]'
                      : 'text-[color:var(--bd-muted)]'
                  }`}
                >
                  {/* A check marks the current language rather than weight
                      alone: at 14px the difference between medium and regular
                      is not a reliable signal, and `aria-checked` already
                      carries it for assistive tech — the mark is the sighted
                      equivalent, not a second source of truth. */}
                  <span
                    aria-hidden="true"
                    className={`w-3.5 shrink-0 text-[color:var(--bd-link)] ${isActive ? '' : 'opacity-0'}`}
                  >
                    ✓
                  </span>
                  {meta.nativeName}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
